import { useState, useEffect } from "react";
import "@/App.css";
import axios from "axios";
import { Toaster, toast } from "sonner";
import {
  Phone,
  Mail,
  MapPin,
  Heart,
  Home,
  Utensils,
  Car,
  Pill,
  UserRound,
  Sun,
  HandHelping,
  CheckCircle2,
  Menu,
  X,
  Shield,
  Clock,
  Users,
  ArrowRight,
  Star,
  Quote,
  ShowerHead,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LOGO_URL = "https://customer-assets.emergentagent.com/job_senior-support-yeg/artifacts/4ifv3m49_logo.png";
const HERO_IMG = "https://images.unsplash.com/photo-1773227055624-07b515ba87c5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTJ8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjBjYXJlZ2l2ZXIlMjB3aXRoJTIwc2VuaW9yJTIwY2l0aXplbiUyMGF0JTIwaG9tZSUyMHN1cHBvcnR8ZW58MHx8fHwxNzc0MzgwNzk0fDA&ixlib=rb-4.1.0&q=85";
const ABOUT_IMG = "https://images.pexels.com/photos/6646773/pexels-photo-6646773.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const PHONE = "+1 (780) 982-4445";
const PHONE_LINK = "tel:+17809824445";
const EMAIL = "jc3392138@gmail.com";

// Navigation
function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { label: "About", href: "#about" },
    { label: "Services", href: "#services" },
    { label: "Why Us", href: "#why-us" },
    { label: "Contact", href: "#contact" },
  ];

  const handleNavClick = (href) => {
    setMobileOpen(false);
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <>
      <header
        data-testid="main-navigation"
        className={`fixed top-0 left-0 right-0 z-50 bg-white transition-shadow duration-300 ${
          scrolled ? "nav-scrolled" : ""
        }`}
      >
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <div className="flex items-center justify-between h-16 md:h-20">
            {/* Logo */}
            <a
              href="#hero"
              onClick={(e) => {
                e.preventDefault();
                window.scrollTo({ top: 0, behavior: "smooth" });
              }}
              className="flex items-center gap-2"
              data-testid="nav-logo"
            >
              <img src={LOGO_URL} alt="JC Assisted Living Support" className="h-12 md:h-14 w-auto" />
              <div className="flex flex-col leading-tight">
                <span className="text-[#0F293A] font-bold text-base md:text-lg tracking-tight" style={{ fontFamily: "'Manrope', sans-serif" }}>JC Assisted Living</span>
                <span className="text-[#4AA3DF] text-[10px] md:text-xs font-semibold tracking-[0.25em] uppercase">Support</span>
              </div>
            </a>

            {/* Desktop Nav */}
            <nav className="hidden md:flex items-center gap-8" data-testid="desktop-nav">
              {navLinks.map((link) => (
                <button
                  key={link.href}
                  onClick={() => handleNavClick(link.href)}
                  className="text-[#0F293A] hover:text-[#4AA3DF] font-medium transition-colors text-base"
                  data-testid={`nav-link-${link.label.toLowerCase().replace(' ', '-')}`}
                >
                  {link.label}
                </button>
              ))}
            </nav>

            {/* Desktop Phone CTA */}
            <div className="hidden md:flex items-center gap-4">
              <a
                href={PHONE_LINK}
                data-testid="nav-call-button"
              >
                <Button className="bg-[#4AA3DF] hover:bg-[#358BC3] text-white rounded-full px-6 py-5 text-base font-semibold transition-all hover:-translate-y-0.5">
                  <Phone className="w-4 h-4 mr-2" />
                  Call Now
                </Button>
              </a>
            </div>

            {/* Mobile Menu Toggle */}
            <button
              className="md:hidden p-2 text-[#0F293A]"
              onClick={() => setMobileOpen(!mobileOpen)}
              data-testid="mobile-menu-toggle"
              aria-label="Toggle mobile menu"
            >
              {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileOpen && (
          <div className="md:hidden bg-white border-t border-slate-100 pb-4" data-testid="mobile-menu">
            <nav className="container mx-auto px-4 flex flex-col gap-1 pt-2">
              {navLinks.map((link) => (
                <button
                  key={link.href}
                  onClick={() => handleNavClick(link.href)}
                  className="text-left py-3 px-4 text-[#0F293A] hover:bg-[#EBF5FA] rounded-lg font-medium transition-colors"
                  data-testid={`mobile-nav-link-${link.label.toLowerCase().replace(' ', '-')}`}
                >
                  {link.label}
                </button>
              ))}
              <a href={PHONE_LINK} className="mt-2 px-4" data-testid="mobile-call-link">
                <Button className="w-full bg-[#4AA3DF] hover:bg-[#358BC3] text-white rounded-full py-5 font-semibold">
                  <Phone className="w-4 h-4 mr-2" />
                  {PHONE}
                </Button>
              </a>
            </nav>
          </div>
        )}
      </header>

      {/* Mobile Sticky Bottom Bar */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 z-50 bg-white mobile-bottom-bar border-t border-slate-100 px-4 py-3" data-testid="mobile-bottom-bar">
        <a href={PHONE_LINK}>
          <Button className="w-full bg-[#4AA3DF] hover:bg-[#358BC3] text-white rounded-full py-5 text-base font-semibold">
            <Phone className="w-5 h-5 mr-2" />
            Call Now: {PHONE}
          </Button>
        </a>
      </div>
    </>
  );
}

// Hero Section
function HeroSection() {
  return (
    <section id="hero" className="pt-20 md:pt-24 pb-16 md:pb-24 bg-white" data-testid="hero-section">
      <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          {/* Left: Text */}
          <div className="animate-fade-in-up">
            <p className="text-sm uppercase tracking-widest font-bold text-[#4AA3DF] mb-4">Edmonton Home Care Services</p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.1] text-[#0F293A] mb-6">
              Trusted Assisted Living Support in Edmonton
            </h1>
            <p className="text-lg md:text-xl leading-relaxed text-slate-600 mb-8">
              Helping seniors, people with disabilities, and individuals live safely and comfortably at home.
            </p>

            {/* Bullet Points */}
            <div className="flex flex-wrap gap-4 mb-10">
              {["Personal Care", "Companionship", "Flexible Hours"].map((item) => (
                <span
                  key={item}
                  className="flex items-center gap-2 bg-[#EBF5FA] text-[#1E4059] px-4 py-2 rounded-full text-sm font-semibold"
                >
                  <CheckCircle2 className="w-4 h-4 text-[#4AA3DF]" />
                  {item}
                </span>
              ))}
            </div>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-4">
              <a href={PHONE_LINK} data-testid="hero-call-button">
                <Button className="bg-[#4AA3DF] hover:bg-[#358BC3] text-white rounded-full px-8 py-6 text-lg font-semibold shadow-lg transition-all hover:-translate-y-1 w-full sm:w-auto">
                  <Phone className="w-5 h-5 mr-2" />
                  Call Now: {PHONE}
                </Button>
              </a>
              <button
                onClick={() => document.querySelector("#contact")?.scrollIntoView({ behavior: "smooth" })}
                data-testid="hero-consultation-button"
              >
                <Button
                  variant="outline"
                  className="border-2 border-[#4AA3DF] text-[#4AA3DF] hover:bg-[#EBF5FA] rounded-full px-8 py-6 text-lg font-semibold transition-all w-full sm:w-auto"
                >
                  Free Consultation
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </button>
            </div>
          </div>

          {/* Right: Image */}
          <div className="animate-fade-in-up animate-delay-300 flex justify-center lg:justify-end">
            <div className="hero-float relative">
              <img
                src={HERO_IMG}
                alt="Caregiver providing compassionate home care to a senior in Edmonton"
                className="rounded-3xl shadow-xl w-full max-w-lg object-cover aspect-[4/5]"
              />
              <div className="absolute -bottom-4 -right-4 bg-white rounded-2xl shadow-lg p-4 flex items-center gap-3">
                <div className="bg-[#EBF5FA] rounded-full p-2">
                  <Heart className="w-6 h-6 text-[#4AA3DF]" />
                </div>
                <div>
                  <p className="text-sm font-bold text-[#0F293A]">Compassionate Care</p>
                  <p className="text-xs text-slate-500">Serving Edmonton families</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// Trust Bar
function TrustBar() {
  const items = [
    { icon: Shield, text: "Licensed & Insured" },
    { icon: MapPin, text: "Local to Edmonton" },
    { icon: Clock, text: "Flexible Scheduling" },
    { icon: Users, text: "Family-Focused Care" },
  ];

  return (
    <section className="bg-[#F8FAFC] border-y border-slate-100 py-8" data-testid="trust-bar">
      <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
          {items.map((item) => (
            <div key={item.text} className="flex items-center gap-3 justify-center">
              <item.icon className="w-5 h-5 text-[#4AA3DF] flex-shrink-0" />
              <span className="text-sm md:text-base font-semibold text-[#1E4059]">{item.text}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// About Section
function AboutSection() {
  return (
    <section id="about" className="py-20 md:py-32 bg-white" data-testid="about-section">
      <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Image */}
          <div className="animate-fade-in-up">
            <img
              src={ABOUT_IMG}
              alt="Caregiver assisting an elderly person with daily activities"
              className="rounded-3xl shadow-lg w-full object-cover aspect-[4/3]"
            />
          </div>

          {/* Text */}
          <div className="animate-fade-in-up animate-delay-200">
            <p className="text-sm uppercase tracking-widest font-bold text-[#4AA3DF] mb-4">About Us</p>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-6 text-[#0F293A]">
              Our Mission Is Your Comfort
            </h2>
            <p className="text-lg leading-relaxed text-slate-600 mb-6">
              At JC Assisted Living Support, we believe everyone deserves to live with dignity, respect, and the highest quality of care. Our mission is to provide compassionate, personalized support that helps our clients maintain their independence and enjoy life in the comfort of their own homes.
            </p>
            <p className="text-base leading-relaxed text-slate-600 mb-8">
              Whether you or your loved one is an elderly individual needing daily assistance, a person living with a disability, or someone recovering from illness, we tailor our care plans to meet each person's unique needs. Every client is treated like family because that's exactly how we see them.
            </p>

            <div className="flex flex-wrap gap-3">
              {["Dignity First", "Personalized Plans", "Trusted by Families"].map((tag) => (
                <span
                  key={tag}
                  className="bg-[#EBF5FA] text-[#1E4059] px-4 py-2 rounded-full text-sm font-semibold"
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// Services Section
function ServicesSection() {
  const services = [
    {
      icon: ShowerHead,
      title: "Personal Care",
      desc: "Bathing, dressing, grooming, and hygiene support with dignity and respect.",
    },
    {
      icon: HandHelping,
      title: "Mobility Assistance",
      desc: "Safe transfers, walking support, and help with movement throughout the home.",
    },
    {
      icon: Heart,
      title: "Companionship",
      desc: "Friendly conversation, social engagement, and emotional support to brighten every day.",
    },
    {
      icon: Utensils,
      title: "Meal Preparation",
      desc: "Nutritious meals prepared according to dietary needs and personal preferences.",
    },
    {
      icon: Pill,
      title: "Medication Reminders",
      desc: "Timely reminders to ensure medications are taken as prescribed.",
    },
    {
      icon: Home,
      title: "Light Housekeeping",
      desc: "Laundry, tidying, dishes, and maintaining a clean, safe living environment.",
    },
    {
      icon: UserRound,
      title: "Disability Support",
      desc: "Specialized care for individuals with physical or cognitive disabilities.",
    },
    {
      icon: Sun,
      title: "Respite Care",
      desc: "Short-term relief for family caregivers so they can recharge and rest.",
    },
  ];

  return (
    <section id="services" className="py-20 md:py-32 bg-[#F8FAFC]" data-testid="services-section">
      <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
        <div className="text-center mb-16">
          <p className="text-sm uppercase tracking-widest font-bold text-[#4AA3DF] mb-4">What We Offer</p>
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-6 text-[#0F293A]">
            Our Home Care Services
          </h2>
          <p className="text-base md:text-lg text-slate-600 max-w-2xl mx-auto">
            Non-medical home care tailored to your needs. Flexible options available for short-term or long-term care.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {services.map((service, i) => (
            <Card
              key={service.title}
              className={`service-card bg-white border border-slate-100 shadow-sm rounded-2xl animate-fade-in-up animate-delay-${(i + 1) * 100}`}
              data-testid={`service-card-${service.title.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <CardContent className="p-8">
                <div className="bg-[#EBF5FA] rounded-xl w-12 h-12 flex items-center justify-center mb-5">
                  <service.icon className="w-6 h-6 text-[#4AA3DF]" />
                </div>
                <h3 className="text-xl font-semibold mb-3 text-[#0F293A]" style={{ fontFamily: "'Merriweather', serif" }}>
                  {service.title}
                </h3>
                <p className="text-base leading-relaxed text-slate-600">{service.desc}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
}

// Why Choose Us
function WhyChooseUsSection() {
  const reasons = [
    { title: "Reliable, Compassionate Support", desc: "Our caregivers are carefully selected for their warmth, empathy, and dedication to quality care." },
    { title: "Flexible Scheduling", desc: "From a few hours a week to round-the-clock care, we adapt to your schedule and needs." },
    { title: "Personalized Care Plans", desc: "Every care plan is customized to fit individual health conditions, preferences, and goals." },
    { title: "Affordable Options", desc: "Quality home care that respects your budget. Transparent pricing with no hidden fees." },
    { title: "We Treat Every Client Like Family", desc: "You're not just a client to us. We build genuine relationships based on trust and respect." },
  ];

  return (
    <section id="why-us" className="py-20 md:py-32 bg-[#EBF5FA]" data-testid="why-choose-us-section">
      <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <div>
            <p className="text-sm uppercase tracking-widest font-bold text-[#4AA3DF] mb-4">Why Choose Us</p>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-6 text-[#0F293A]">
              Care You Can Count On
            </h2>
            <p className="text-base md:text-lg leading-relaxed text-slate-600 mb-10">
              Choosing the right care provider is one of the most important decisions you'll make for your loved one. Here's why Edmonton families trust JC Assisted Living Support.
            </p>
          </div>

          <div className="space-y-5">
            {reasons.map((reason, i) => (
              <div
                key={reason.title}
                className={`flex gap-4 bg-white rounded-2xl p-6 shadow-sm animate-fade-in-up animate-delay-${(i + 1) * 100}`}
                data-testid={`why-us-item-${i}`}
              >
                <div className="flex-shrink-0 mt-1">
                  <CheckCircle2 className="w-6 h-6 text-[#4AA3DF]" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-[#0F293A] mb-1" style={{ fontFamily: "'Merriweather', serif" }}>
                    {reason.title}
                  </h3>
                  <p className="text-base text-slate-600 leading-relaxed">{reason.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

// Testimonials Section
function TestimonialsSection() {
  const testimonials = [
    {
      name: "Margaret H.",
      relation: "Daughter of Client",
      text: "After my father's stroke, we didn't know where to turn. JC Assisted Living Support gave us peace of mind. Their caregiver treats my dad like her own grandfather. He's happier and more independent than we ever expected.",
      rating: 5,
    },
    {
      name: "David & Karen T.",
      relation: "Family of Client",
      text: "Our mother has dementia and we were overwhelmed trying to care for her alone. The team at JC stepped in with such patience and professionalism. They adapted the care plan as her needs changed. We couldn't be more grateful.",
      rating: 5,
    },
    {
      name: "James R.",
      relation: "Client",
      text: "I'm a 72-year-old veteran living alone after my wife passed. The companionship and meal prep from JC has been a lifesaver. My caregiver feels like a friend, not just someone doing a job. Highly recommend to anyone in Edmonton.",
      rating: 5,
    },
    {
      name: "Susan L.",
      relation: "Mother of Client with Disability",
      text: "Finding reliable disability support in Edmonton was a nightmare until we found JC. They truly understand my son's needs. The flexible scheduling lets me keep working while knowing he's in caring, capable hands.",
      rating: 5,
    },
  ];

  return (
    <section className="py-20 md:py-32 bg-white" data-testid="testimonials-section">
      <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
        <div className="text-center mb-16">
          <p className="text-sm uppercase tracking-widest font-bold text-[#4AA3DF] mb-4">Testimonials</p>
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-6 text-[#0F293A]">
            What Families Are Saying
          </h2>
          <p className="text-base md:text-lg text-slate-600 max-w-2xl mx-auto">
            Real stories from Edmonton families who trust us with the care of their loved ones.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {testimonials.map((t, i) => (
            <div
              key={t.name}
              className={`bg-[#F8FAFC] p-8 md:p-10 rounded-3xl border-l-4 border-[#4AA3DF] animate-fade-in-up animate-delay-${(i + 1) * 100}`}
              data-testid={`testimonial-${i}`}
            >
              <div className="flex gap-1 mb-4">
                {Array.from({ length: t.rating }).map((_, j) => (
                  <Star key={j} className="w-5 h-5 fill-[#F59E0B] text-[#F59E0B]" />
                ))}
              </div>
              <Quote className="w-8 h-8 text-[#4AA3DF]/20 mb-3" />
              <p className="text-base leading-relaxed text-slate-600 mb-6 italic">
                "{t.text}"
              </p>
              <div>
                <p className="text-base font-bold text-[#0F293A]">{t.name}</p>
                <p className="text-sm text-slate-500">{t.relation}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// CTA Banner
function CTABanner() {
  return (
    <section className="py-16 md:py-24 bg-[#4AA3DF]" data-testid="cta-banner">
      <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl text-center">
        <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-4 text-white">
          Need Support for Yourself or a Loved One?
        </h2>
        <p className="text-lg md:text-xl text-white/90 mb-8 max-w-2xl mx-auto">
          Get in touch today for a free consultation — no obligation. We're here to help you find the right care solution.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <a href={PHONE_LINK} data-testid="cta-call-button">
            <Button className="bg-white text-[#4AA3DF] hover:bg-slate-50 rounded-full px-8 py-6 text-lg font-semibold shadow-lg transition-all hover:-translate-y-1">
              <Phone className="w-5 h-5 mr-2" />
              Call Now: {PHONE}
            </Button>
          </a>
          <button
            onClick={() => document.querySelector("#contact")?.scrollIntoView({ behavior: "smooth" })}
            data-testid="cta-contact-button"
          >
            <Button className="bg-transparent border-2 border-white text-white hover:bg-white/10 rounded-full px-8 py-6 text-lg font-semibold transition-all">
              <Mail className="w-5 h-5 mr-2" />
              Send a Message
            </Button>
          </button>
        </div>
      </div>
    </section>
  );
}

// Contact Section
function ContactSection() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in all required fields.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Thank you! We'll be in touch shortly.");
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch {
      toast.error("Something went wrong. Please try calling us directly.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="py-20 md:py-32 bg-white" data-testid="contact-section">
      <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
        <div className="text-center mb-16">
          <p className="text-sm uppercase tracking-widest font-bold text-[#4AA3DF] mb-4">Get In Touch</p>
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-6 text-[#0F293A]">
            Contact Us Today
          </h2>
          <p className="text-base md:text-lg text-slate-600 max-w-2xl mx-auto">
            Ready to learn more about our services? Reach out by phone, email, or fill out the form below for a free, no-obligation consultation.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
          {/* Contact Info */}
          <div className="space-y-8">
            <div className="flex items-start gap-4" data-testid="contact-phone-info">
              <div className="bg-[#EBF5FA] rounded-xl w-12 h-12 flex items-center justify-center flex-shrink-0">
                <Phone className="w-6 h-6 text-[#4AA3DF]" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-[#0F293A] mb-1" style={{ fontFamily: "'Merriweather', serif" }}>Phone</h3>
                <a href={PHONE_LINK} className="text-[#4AA3DF] hover:text-[#358BC3] text-lg font-medium transition-colors">
                  {PHONE}
                </a>
                <p className="text-sm text-slate-500 mt-1">Call us anytime</p>
              </div>
            </div>

            <div className="flex items-start gap-4" data-testid="contact-email-info">
              <div className="bg-[#EBF5FA] rounded-xl w-12 h-12 flex items-center justify-center flex-shrink-0">
                <Mail className="w-6 h-6 text-[#4AA3DF]" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-[#0F293A] mb-1" style={{ fontFamily: "'Merriweather', serif" }}>Email</h3>
                <a href={`mailto:${EMAIL}`} className="text-[#4AA3DF] hover:text-[#358BC3] text-lg font-medium transition-colors">
                  {EMAIL}
                </a>
                <p className="text-sm text-slate-500 mt-1">We respond within 24 hours</p>
              </div>
            </div>

            <div className="flex items-start gap-4" data-testid="contact-location-info">
              <div className="bg-[#EBF5FA] rounded-xl w-12 h-12 flex items-center justify-center flex-shrink-0">
                <MapPin className="w-6 h-6 text-[#4AA3DF]" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-[#0F293A] mb-1" style={{ fontFamily: "'Merriweather', serif" }}>Location</h3>
                <p className="text-lg text-slate-700">Edmonton, Alberta</p>
                <p className="text-sm text-slate-500 mt-1">Serving Edmonton and nearby communities</p>
              </div>
            </div>

            <Separator className="my-6" />

            <div className="bg-[#EBF5FA] rounded-2xl p-6">
              <p className="text-[#1E4059] font-semibold mb-2" style={{ fontFamily: "'Merriweather', serif" }}>Free Consultation</p>
              <p className="text-slate-600 text-sm leading-relaxed">
                Not sure what type of care you need? Let us help. Our consultations are completely free with no obligation. We'll listen to your situation and recommend the best care plan for your family.
              </p>
            </div>
          </div>

          {/* Contact Form */}
          <div>
            <Card className="bg-white border border-slate-200 rounded-2xl shadow-sm" data-testid="contact-form-card">
              <CardContent className="p-8">
                <h3 className="text-xl font-semibold text-[#0F293A] mb-6" style={{ fontFamily: "'Merriweather', serif" }}>
                  Send Us a Message
                </h3>
                <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-[#0F293A] mb-2">
                      Full Name <span className="text-red-500">*</span>
                    </label>
                    <Input
                      id="name"
                      name="name"
                      type="text"
                      placeholder="Your full name"
                      value={form.name}
                      onChange={handleChange}
                      required
                      className="h-12 px-4 bg-white border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#4AA3DF] focus:border-transparent transition-all text-base"
                      data-testid="contact-input-name"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-[#0F293A] mb-2">
                      Email Address <span className="text-red-500">*</span>
                    </label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      placeholder="your@email.com"
                      value={form.email}
                      onChange={handleChange}
                      required
                      className="h-12 px-4 bg-white border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#4AA3DF] focus:border-transparent transition-all text-base"
                      data-testid="contact-input-email"
                    />
                  </div>
                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-[#0F293A] mb-2">
                      Phone Number
                    </label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      placeholder="(780) 123-4567"
                      value={form.phone}
                      onChange={handleChange}
                      className="h-12 px-4 bg-white border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#4AA3DF] focus:border-transparent transition-all text-base"
                      data-testid="contact-input-phone"
                    />
                  </div>
                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-[#0F293A] mb-2">
                      Message <span className="text-red-500">*</span>
                    </label>
                    <Textarea
                      id="message"
                      name="message"
                      placeholder="Tell us about your care needs..."
                      rows={4}
                      value={form.message}
                      onChange={handleChange}
                      required
                      className="px-4 py-3 bg-white border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#4AA3DF] focus:border-transparent transition-all text-base resize-none"
                      data-testid="contact-input-message"
                    />
                  </div>
                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-[#4AA3DF] hover:bg-[#358BC3] text-white rounded-full py-6 text-lg font-semibold shadow-lg transition-all hover:-translate-y-0.5 disabled:opacity-50"
                    data-testid="contact-submit-button"
                  >
                    {loading ? "Sending..." : "Send Message"}
                    {!loading && <ArrowRight className="w-5 h-5 ml-2" />}
                  </Button>
                  <p className="text-xs text-slate-400 text-center mt-3">
                    We respect your privacy. Your information will never be shared.
                  </p>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
}

// Footer
function Footer() {
  return (
    <footer className="bg-[#0F293A] text-white py-12 md:py-16 pb-24 md:pb-16" data-testid="footer">
      <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
          {/* Logo / About */}
          <div>
            <div className="flex items-center gap-3 mb-4">
              <img src={LOGO_URL} alt="JC Assisted Living Support" className="h-14 w-auto" />
              <div className="flex flex-col leading-tight">
                <span className="text-white font-bold text-lg tracking-tight" style={{ fontFamily: "'Manrope', sans-serif" }}>JC Assisted Living</span>
                <span className="text-[#4AA3DF] text-xs font-semibold tracking-[0.25em] uppercase">Support</span>
              </div>
            </div>
            <p className="text-slate-300 text-sm leading-relaxed">
              Compassionate in-home support for seniors, individuals with disabilities, and those needing daily living assistance in Edmonton, Alberta.
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-lg font-semibold mb-4" style={{ fontFamily: "'Merriweather', serif" }}>Quick Links</h4>
            <nav className="space-y-2">
              {[
                { label: "About Us", href: "#about" },
                { label: "Our Services", href: "#services" },
                { label: "Why Choose Us", href: "#why-us" },
                { label: "Contact", href: "#contact" },
              ].map((link) => (
                <button
                  key={link.href}
                  onClick={() => document.querySelector(link.href)?.scrollIntoView({ behavior: "smooth" })}
                  className="block text-slate-300 hover:text-[#4AA3DF] transition-colors text-sm"
                >
                  {link.label}
                </button>
              ))}
            </nav>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-lg font-semibold mb-4" style={{ fontFamily: "'Merriweather', serif" }}>Contact</h4>
            <div className="space-y-3">
              <a href={PHONE_LINK} className="flex items-center gap-2 text-slate-300 hover:text-[#4AA3DF] transition-colors text-sm" data-testid="footer-phone">
                <Phone className="w-4 h-4" />
                {PHONE}
              </a>
              <a href={`mailto:${EMAIL}`} className="flex items-center gap-2 text-slate-300 hover:text-[#4AA3DF] transition-colors text-sm" data-testid="footer-email">
                <Mail className="w-4 h-4" />
                {EMAIL}
              </a>
              <span className="flex items-center gap-2 text-slate-300 text-sm">
                <MapPin className="w-4 h-4" />
                Edmonton, Alberta
              </span>
            </div>
          </div>
        </div>

        <Separator className="my-8 bg-slate-700" />

        <div className="flex flex-col md:flex-row justify-between items-center gap-4 text-sm text-slate-400">
          <p>&copy; {new Date().getFullYear()} JC Assisted Living Support. All rights reserved.</p>
          <p>Serving Edmonton and surrounding communities.</p>
        </div>
      </div>
    </footer>
  );
}


function App() {
  return (
    <div className="min-h-screen bg-white">
      <Toaster position="top-right" richColors />
      <Navbar />
      <main>
        <HeroSection />
        <TrustBar />
        <AboutSection />
        <ServicesSection />
        <WhyChooseUsSection />
        <TestimonialsSection />
        <CTABanner />
        <ContactSection />
      </main>
      <Footer />
    </div>
  );
}

export default App;
